// Matches "Awaiting fee slip" billing items (appearances where counsel appeared) to the fee
// slips the email intake has filed (api/email-intake.js).
//
// Intake rows carry no tidy client name: their client_name is usually the email subject,
// e.g. "Invoice 00160353 for Briana Proud in the matter of Jane CITIZEN". So a slip matches
// when its subject names the client - surname and first name as whole words - and it
// arrived on or after the appearance. Clerk emails that name no client ("Greens List
// Barristers - Tax Invoice INV-...") cannot be matched this way and are left alone.

const words = (value = "") => ` ${String(value).toLowerCase().normalize("NFKD").replace(/[^a-z]+/g, " ").trim()} `;

// "GRIMA, Karli" or "Karli Grima" -> { surname: "grima", given: "karli" }
export function splitClientName(name = "") {
  const text = String(name).replace(/\([^)]*\)/g, " ").trim();
  if (!text) return null;
  if (text.includes(",")) {
    const [surname, rest = ""] = text.split(",");
    return { surname: words(surname).trim(), given: words(rest).trim().split(" ")[0] || "" };
  }
  const parts = words(text).trim().split(" ").filter(Boolean);
  if (parts.length === 0) return null;
  return { surname: parts[parts.length - 1], given: parts.length > 1 ? parts[0] : "" };
}

// "28/09/2026" -> start of that day, local time; null if unreadable.
export function appearanceDate(value = "") {
  const match = String(value).match(/(\d{1,2})\/(\d{1,2})\/(\d{2,4})/);
  if (!match) return null;
  const year = match[3].length === 2 ? 2000 + Number(match[3]) : Number(match[3]);
  return new Date(year, Number(match[2]) - 1, Number(match[1]));
}

const DISBURSEMENT = /disbursement/i;

export function slipNamesClient(slip, client) {
  if (!client?.surname) return false;
  const text = words(`${slip?.subject || ""} ${slip?.client_name || ""}`);
  if (!text.includes(` ${client.surname} `)) return false;
  return !client.given || text.includes(` ${client.given} `);
}

export function slipSummary(slip) {
  return {
    id: slip.id,
    subject: slip.subject || slip.client_name || "",
    sender: slip.sender || "",
    amount: slip.amount != null ? Number(slip.amount) : null,
    receivedAt: slip.received_at || null,
    filePath: slip.file_path || null,
  };
}

// Links for a matched fee slip: its own PDF (as filed by the intake) and the backsheet from
// the same email, fetched on demand. The intake row is preferred because a stored summary
// from before links existed has no file path.
export function feeSlipLinks(feeSlip, intakeRow) {
  if (!feeSlip?.id) return { slipUrl: "", backsheetUrl: "" };
  const filePath = intakeRow?.file_purged_at ? "" : (intakeRow?.file_path || feeSlip.filePath || "");
  return {
    slipUrl: filePath ? `/api/files?path=${encodeURIComponent(filePath)}` : "",
    backsheetUrl: `/api/email-intake?backsheet=${encodeURIComponent(feeSlip.id)}`,
  };
}

// Returns Map(pendingKey -> fee slip summary) for awaiting items that now have a slip.
// Items already linked keep their slip, and a slip is never given to two matters; the
// earliest appearance takes the earliest matching slip.
export function matchFeeSlips(items = [], slips = []) {
  const used = new Set((items || []).map(item => item.feeSlip?.id).filter(Boolean));
  const candidates = (slips || [])
    .filter(slip => slip?.id && slip.received_at && !DISBURSEMENT.test(slip.subject || ""))
    .sort((a, b) => String(a.received_at).localeCompare(String(b.received_at)));

  const waiting = (items || [])
    .filter(item => !item.feeSlip)
    .sort((a, b) => (appearanceDate(a.date)?.getTime() ?? Infinity) - (appearanceDate(b.date)?.getTime() ?? Infinity));

  const matches = new Map();
  for (const item of waiting) {
    const client = splitClientName(item.client_name);
    const from = appearanceDate(item.date);
    const rejected = new Set(item.rejectedFeeSlips || []);
    const slip = candidates.find(candidate =>
      !used.has(candidate.id)
      && !rejected.has(candidate.id)
      && (!from || new Date(candidate.received_at) >= from)
      && slipNamesClient(candidate, client)
    );
    if (slip) {
      used.add(slip.id);
      matches.set(item.pendingKey, slipSummary(slip));
    }
  }
  return matches;
}
